import { UserRole } from '../../../shared/enums/user-role.enum';
import {
  APP_ROUTES,
  BLOG_ROUTES,
  DASHBOARD_PERMITTED_ROLES
} from '../../../shared/config/routes-config';
import { RouteConfig } from '../../../shared/interfaces/routes-config.interface';
import { hasRole } from '../../../shared/services/helpers/auth-helper/auth-helper.service';

export type LeadVertical = 'reservation' | 'acquisition' | 'furnishing';

export type TeamPanel =
  | 'doubleBookings'
  | 'arrivals'
  | 'overduePayments'
  | 'housekeeping'
  | 'guestCalls';

export type GoalKey = 'ticketsClosedToday' | 'awaitingFirstResponse' | 'leadsPastSla' | 'newLeads';

export interface DashboardGoal {
  key: GoalKey;
  label: string;
  target: number;
  lowerIsBetter?: boolean;
}

export type RoleDashboard =
  | { kind: 'admin'; superAdmin: boolean }
  | { kind: 'redirect'; path: string }
  | {
      kind: 'team';
      verticals: LeadVertical[];
      panels: TeamPanel[];
      goals: DashboardGoal[];
    };

const LEAD_TEAMS: { roles: UserRole[]; verticals: LeadVertical[] }[] = [
  {
    roles: [UserRole.RESERVATIONS, UserRole.RESERVATION_MANAGER, UserRole.RESERVATION_AGENT],
    verticals: ['reservation']
  },
  {
    roles: [
      UserRole.SALES_SPECIALIST,
      UserRole.SALES_MANAGER,
      UserRole.SALES_PERSON,
      UserRole.COMMERCIAL_MANAGER
    ],
    verticals: ['acquisition', 'furnishing']
  }
];

const TEAM_PANELS: { roles: UserRole[]; panel: TeamPanel }[] = [
  {
    roles: [UserRole.RESERVATIONS, UserRole.RESERVATION_MANAGER, UserRole.RESERVATION_AGENT],
    panel: 'doubleBookings'
  },
  {
    roles: [UserRole.RESERVATIONS, UserRole.RESERVATION_MANAGER, UserRole.RESERVATION_AGENT],
    panel: 'arrivals'
  },
  { roles: [UserRole.FINANCE_MANAGER, UserRole.FINANCE_SPECIALIST], panel: 'overduePayments' },
  {
    roles: [UserRole.HOUSE_KEEPING_MANAGER, UserRole.HOUSE_KEEPING],
    panel: 'housekeeping'
  },
  {
    roles: [UserRole.HOSPITALITY_AGENT, UserRole.HOSPITALITY_MANAGER, UserRole.GES],
    panel: 'guestCalls'
  }
];

const REDIRECTS: { roles: UserRole[]; path: string }[] = [
  {
    roles: [UserRole.MARKETING_SPECIALIST, UserRole.MARKETING_CAMPAIGNS],
    path: BLOG_ROUTES.FULL_PATH
  },
  {
    roles: [
      UserRole.E_COMMERCE,
      UserRole.ECOMMERCE_SPECIALIST,
      UserRole.ECOMMERCE_SENIOR_SPECIALIST
    ],
    path: (APP_ROUTES.UNIT_TYPE_MODULE.CHILDREN as RouteConfig).LIST.FULL_PATH
  },
  {
    roles: [UserRole.DESIGN_TEAM, UserRole.DESIGN_MANAGER],
    path: (APP_ROUTES.FURNISHING_MANAGEMENT_MODULE.CHILDREN as RouteConfig).PACKAGES.FULL_PATH
  },
  { roles: [UserRole.OPERATION_MANAGER], path: APP_ROUTES.OPERATION_MODULE.FULL_PATH },
  {
    roles: [UserRole.HOSPITALITY_LEAD],
    path: (APP_ROUTES.RESERVATION_MODULE.CHILDREN as RouteConfig).LIST.FULL_PATH
  }
];

const REDIRECTED_ROLES = REDIRECTS.reduce<UserRole[]>((all, r) => all.concat(r.roles), []);

export const ROLE_DASHBOARD_PERMITTED_ROLES: UserRole[] = DASHBOARD_PERMITTED_ROLES.filter(
  (role) => role !== UserRole.INVESTOR && !REDIRECTED_ROLES.includes(role)
).concat(REDIRECTED_ROLES);

// someone holding a redirected role plus a dashboard one keeps the dashboard
const DASHBOARD_ROLES_WITH_A_DASHBOARD = DASHBOARD_PERMITTED_ROLES.filter(
  (role) => !REDIRECTED_ROLES.includes(role)
);

const TICKET_GOALS: DashboardGoal[] = [
  { key: 'ticketsClosedToday', label: 'Tickets closed today', target: 10 },
  { key: 'awaitingFirstResponse', label: 'Awaiting first response', target: 3, lowerIsBetter: true }
];

const LEAD_GOALS: DashboardGoal[] = [
  { key: 'leadsPastSla', label: 'Leads past SLA', target: 0, lowerIsBetter: true },
  { key: 'newLeads', label: 'New leads not contacted', target: 5, lowerIsBetter: true }
];

const teamPanels = () => TEAM_PANELS.filter((t) => hasRole(t.roles)).map((t) => t.panel);

export function resolveRoleDashboard(): RoleDashboard {
  if (hasRole([UserRole.SUPER_ADMIN, UserRole.ADMIN])) {
    return { kind: 'admin', superAdmin: hasRole([UserRole.SUPER_ADMIN]) };
  }
  const verticals = LEAD_TEAMS.filter((team) => hasRole(team.roles)).reduce<LeadVertical[]>(
    (all, team) => all.concat(team.verticals),
    []
  );
  if (verticals.length) {
    return {
      kind: 'team',
      verticals,
      panels: teamPanels(),
      goals: [...LEAD_GOALS, ...TICKET_GOALS]
    };
  }
  const redirect = REDIRECTS.find((r) => hasRole(r.roles));
  if (redirect && !hasRole(DASHBOARD_ROLES_WITH_A_DASHBOARD)) {
    return { kind: 'redirect', path: redirect.path };
  }
  return { kind: 'team', verticals: [], panels: teamPanels(), goals: TICKET_GOALS };
}
